/**
 * Which languages exist, how to load one, and which way it reads.
 *
 * Catalogues are loaded through a static map of dynamic imports so Vite can see
 * every branch and split each language into its own chunk — a French planner
 * never downloads the Chinese strings.
 */
import type { Catalogue } from './locales/en'

export const LOCALES = ['en', 'az', 'tr', 'ru', 'ar', 'es', 'zh', 'fr'] as const

export type LocaleCode = (typeof LOCALES)[number]

/** Each language named in itself — a menu of endonyms is the one people can read. */
export const LOCALE_NAMES: Record<LocaleCode, string> = {
  en: 'English',
  az: 'Azərbaycanca',
  tr: 'Türkçe',
  ru: 'Русский',
  ar: 'العربية',
  es: 'Español',
  zh: '中文',
  fr: 'Français',
}

const RTL: ReadonlySet<LocaleCode> = new Set<LocaleCode>(['ar'])

export type Direction = 'ltr' | 'rtl'

export const directionOf = (locale: LocaleCode): Direction => (RTL.has(locale) ? 'rtl' : 'ltr')

export const isLocale = (value: string): value is LocaleCode =>
  (LOCALES as readonly string[]).includes(value)

const LOADERS: Record<LocaleCode, () => Promise<{ default: Catalogue }>> = {
  en: () => import('./locales/en'),
  az: () => import('./locales/az'),
  tr: () => import('./locales/tr'),
  ru: () => import('./locales/ru'),
  ar: () => import('./locales/ar'),
  es: () => import('./locales/es'),
  zh: () => import('./locales/zh'),
  fr: () => import('./locales/fr'),
}

export const loadCatalogue = async (locale: LocaleCode): Promise<Catalogue> =>
  (await LOADERS[locale]()).default

/**
 * Best match for what the browser asks for.
 *
 * Matches on the primary subtag, so `ru-RU`, `es-419` and `zh-Hans-CN` all
 * land somewhere sensible instead of falling through to English.
 */
export function resolveLocale(preferred: readonly string[]): LocaleCode {
  for (const tag of preferred) {
    const primary = tag.toLowerCase().split('-')[0]
    if (isLocale(primary)) return primary
  }
  return 'en'
}

export type { Catalogue }
